import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Eye, Trash, MagnifyingGlass, PencilSimple, CopySimple } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DocumentList({ apiPath, numberField, title, createPath, viewPath, editPath }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchDocs = useCallback(async () => {
    try { const res = await axios.get(`${API}/${apiPath}`); setDocs(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [apiPath]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm(`Delete this ${title.toLowerCase().slice(0, -1)}?`)) return;
    try { await axios.delete(`${API}/${apiPath}/${id}`); fetchDocs(); }
    catch (e) { alert('Failed to delete'); }
  }, [apiPath, fetchDocs, title]);

  const handleDuplicate = useCallback(async (id) => {
    try { await axios.post(`${API}/${apiPath}/${id}/duplicate`); fetchDocs(); }
    catch (e) { console.error(e); alert('Failed to duplicate'); }
  }, [apiPath, fetchDocs]);

  const filtered = docs.filter(d =>
    (d[numberField] || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.company_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.customer?.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.user_name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl text-stone-900" data-testid={`${apiPath}-list-title`}>{title}</h1>
          <p className="text-sm text-stone-500 mt-1">{docs.length} total</p>
        </div>
        <Link to={createPath} data-testid={`create-${apiPath}-button`}
          className="inline-flex items-center gap-2 bg-[#002FA7] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#002480] transition-all active:scale-[0.97]">
          <Plus size={16} weight="bold" /> New {title.slice(0, -1)}
        </Link>
      </div>

      <div className="relative mb-6">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input type="text" data-testid={`search-${apiPath}`} placeholder="Search by number, company, customer..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-96 bg-white border border-stone-200 pl-9 pr-4 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none transition-all" />
      </div>

      {loading ? (
        <div className="py-20 text-center text-stone-400 text-sm">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-stone-200 py-16 text-center">
          <p className="text-stone-400 text-sm mb-3">No {title.toLowerCase()} found</p>
          <Link to={createPath} className="text-[#002FA7] text-sm font-semibold hover:underline">Create your first one</Link>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid={`${apiPath}-table`}>
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <th className="py-3 px-4 font-semibold text-stone-600 text-xs uppercase tracking-wider">No.</th>
                <th className="py-3 px-4 font-semibold text-stone-600 text-xs uppercase tracking-wider">Date</th>
                <th className="py-3 px-4 font-semibold text-stone-600 text-xs uppercase tracking-wider">Customer</th>
                <th className="py-3 px-4 font-semibold text-stone-600 text-xs uppercase tracking-wider">Company</th>
                <th className="py-3 px-4 font-semibold text-stone-600 text-xs uppercase tracking-wider">By</th>
                <th className="py-3 px-4 font-semibold text-stone-600 text-xs uppercase tracking-wider text-right">Total</th>
                <th className="py-3 px-4 font-semibold text-stone-600 text-xs uppercase tracking-wider text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={d.id} className="border-b border-stone-100 hover:bg-stone-50/50 transition-colors">
                  <td className="py-3 px-4 font-semibold text-stone-900 whitespace-nowrap">{d[numberField]}</td>
                  <td className="py-3 px-4 text-stone-600 whitespace-nowrap">{d.date}</td>
                  <td className="py-3 px-4 text-stone-700">{d.customer?.name || '\u2014'}</td>
                  <td className="py-3 px-4 text-stone-600">{d.company_name}</td>
                  <td className="py-3 px-4 text-stone-600">{d.user_name}</td>
                  <td className="py-3 px-4 text-stone-900 font-semibold text-right whitespace-nowrap">Rs. {d.grand_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center gap-0.5">
                      <Link to={`${viewPath}/${d.id}`} className="p-1.5 text-stone-400 hover:text-[#002FA7] hover:bg-stone-100 transition-colors" title="View"><Eye size={15} /></Link>
                      <Link to={`${editPath}/${d.id}`} className="p-1.5 text-stone-400 hover:text-[#002FA7] hover:bg-stone-100 transition-colors" title="Edit"><PencilSimple size={15} /></Link>
                      <button onClick={() => handleDuplicate(d.id)} className="p-1.5 text-stone-400 hover:text-green-600 hover:bg-green-50 transition-colors" title="Duplicate"><CopySimple size={15} /></button>
                      <button onClick={() => handleDelete(d.id)} className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete"><Trash size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
